"use client";

import React, { useState, useEffect, useRef } from "react";
import { X, Info } from "lucide-react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// Mock data structure - replace with actual API call
interface CountyData {
	state: string;
	county: string;
	totalLiens: number;
	coordinates: [number, number]; // [lng, lat]
	percentChange?: number; // Month over month change
	fips?: string; // County FIPS code for mapping
	lastUpdated: string; // Last record update
	propertiesWithParcelData: number; // Count of properties with parcel data
}

interface MapStats {
	totalProperties: number;
	totalCounties: number;
	totalStates: number;
	lastUpdated: string;
	coverage: number; // Percentage of US counties covered
}

// Mock county data - replace with your API response
const mockCountyData: CountyData[] = [
	{
		state: "FL",
		county: "Miami-Dade",
		totalLiens: 1250,
		coordinates: [-80.19, 25.76],
		percentChange: 12,
		fips: "12086",
		lastUpdated: "2025-11-29T08:00:00Z",
		propertiesWithParcelData: 1000,
	},
	{
		state: "FL",
		county: "Hillsborough",
		totalLiens: 890,
		coordinates: [-82.45, 27.99],
		percentChange: 8,
		fips: "12057",
		lastUpdated: "2025-11-29T08:00:00Z",
		propertiesWithParcelData: 800,
	},
	{
		state: "TX",
		county: "Harris",
		totalLiens: 1100,
		coordinates: [-95.36, 29.76],
		percentChange: 15,
		fips: "48201",
		lastUpdated: "2025-11-29T08:00:00Z",
		propertiesWithParcelData: 1200,
	},
	{
		state: "TX",
		county: "Travis",
		totalLiens: 650,
		coordinates: [-97.74, 30.27],
		percentChange: 10,
		fips: "48453",
		lastUpdated: "2025-11-29T08:00:00Z",
		propertiesWithParcelData: 600,
	},
	{
		state: "AZ",
		county: "Maricopa",
		totalLiens: 980,
		coordinates: [-112.07, 33.45],
		percentChange: 18,
		fips: "04013",
		lastUpdated: "2025-11-29T08:00:00Z",
		propertiesWithParcelData: 900,
	},
	{
		state: "CA",
		county: "Orange",
		totalLiens: 720,
		coordinates: [-117.83, 33.71],
		percentChange: 5,
		fips: "06059",
		lastUpdated: "2025-11-29T08:00:00Z",
		propertiesWithParcelData: 700,
	},
	{
		state: "NV",
		county: "Clark",
		totalLiens: 850,
		coordinates: [-115.13, 36.17],
		percentChange: 22,
		fips: "32003",
		lastUpdated: "2025-11-29T08:00:00Z",
		propertiesWithParcelData: 850,
	},
];

export default function USCoverageMapMobile() {
	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<mapboxgl.Map | null>(null);
	const [selectedRegion, setSelectedRegion] = useState<CountyData | null>(
		null
	);
	const [mapStats, setMapStats] = useState<MapStats | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	// Handle ESC key to close modal
	useEffect(() => {
		if (!selectedRegion) return;

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setSelectedRegion(null);
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("keydown", handleEscape);
		};
	}, [selectedRegion]);

	// Fetch map data from API
	useEffect(() => {
		// TODO: Replace with actual API call
		const fetchMapData = async () => {
			try {
				setIsLoading(true);

				// Mock data for demonstration
				const mockStats: MapStats = {
					totalProperties: 45678,
					totalCounties: 234,
					totalStates: 28,
					lastUpdated: "2025-11-29T08:00:00Z",
					coverage: 7.4, // 234 out of ~3,143 US counties
				};

				setMapStats(mockStats);
				setIsLoading(false);
			} catch (error) {
				console.error("Failed to fetch coverage data:", error);
				setIsLoading(false);
			}
		};

		fetchMapData();
	}, []);

	// Initialize Mapbox
	useEffect(() => {
		if (!mapContainerRef.current) return;

		// Get token from environment (client-side)
		const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || "";

		// Check if token is set
		if (!token || token.trim() === "" || token.includes("example")) {
			console.warn(
				"Mapbox token not found. Please add NEXT_PUBLIC_MAPBOX_TOKEN to your .env file"
			);
			setIsLoading(false);
			return;
		}

		mapboxgl.accessToken = token;

		const map = new mapboxgl.Map({
			container: mapContainerRef.current,
			style: "mapbox://styles/mapbox/dark-v11",
			center: [-98.5795, 39.8283], // Center of US
			zoom: 3.5,
			projection: "mercator",
		});

		mapRef.current = map;

		map.on("load", () => {
			setIsLoading(false);

			// Add county boundaries source
			map.addSource("counties", {
				type: "vector",
				url: "mapbox://mapbox.82pkq93d",
			});

			// Add county fill layer with choropleth
			map.addLayer({
				id: "county-fills",
				type: "fill",
				source: "counties",
				"source-layer": "original",
				paint: {
					"fill-color": "#2a3f32",
					"fill-opacity": 0.5,
				},
			});

			// Add county borders
			map.addLayer({
				id: "county-borders",
				type: "line",
				source: "counties",
				"source-layer": "original",
				paint: {
					"line-color": "#16251c",
					"line-width": 0.5,
					"line-opacity": 0.5,
				},
			});

			// Add state boundaries
			map.addSource("states", {
				type: "geojson",
				data: "https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_110m_admin_1_states_provinces.geojson",
			});

			map.addLayer({
				id: "state-borders",
				type: "line",
				source: "states",
				paint: {
					"line-color": "#A7FF5E",
					"line-width": 1.5,
					"line-opacity": 0.4,
				},
			});

			// Add markers for counties with data
			mockCountyData.forEach((county) => {
				// Determine color based on property count
				let color = "#cbf7a6";
				if (county.totalLiens > 500) {
					color = "#A7FF5E";
				} else if (county.totalLiens > 100) {
					color = "#cbf7a6";
				} else {
					color = "#8ba894";
				}

				// Create a pulsing circle marker
				const el = document.createElement("div");
				el.className = "county-marker";
				el.style.width = "20px";
				el.style.height = "20px";
				el.style.borderRadius = "50%";
				el.style.backgroundColor = color;
				el.style.border = `2px solid ${color}`;
				el.style.cursor = "pointer";
				el.style.boxShadow = `0 0 20px ${color}`;
				el.style.transition = "all 0.3s ease";

				// Add click handler
				el.addEventListener("click", () => {
					setSelectedRegion(county);
					// Zoom to the county with smooth animation
					if (mapRef.current) {
						mapRef.current.flyTo({
							center: county.coordinates,
							zoom: 10, // Zoom in to see property-level details
							duration: 2000, // 2 second animation
							essential: true,
						});
					}
				});

				// Add marker to map
				new mapboxgl.Marker(el)
					.setLngLat(county.coordinates)
					.addTo(map);
			});
		});

		map.on("error", (e) => {
			console.error("Mapbox error:", e);
			setIsLoading(false);
		});

		return () => {
			map.remove();
		};
	}, []);

	const formatNumber = (num: number) => {
		return num.toLocaleString("en-US");
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffHours = Math.floor(
			(now.getTime() - date.getTime()) / (1000 * 60 * 60)
		);

		if (diffHours < 24) {
			return `Updated ${diffHours} hours ago`;
		} else if (diffHours < 48) {
			return "Updated yesterday";
		} else {
			return `Updated ${date.toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
			})}`;
		}
	};

	return (
		<div className="min-h-screen text-white">
			{/* Top Stats Bar - Scrollable on Mobile */}
			<div className="border-b border-[#2a3f32]/30 sticky top-0 z-30">
				<div className="py-4">
					<div className="flex flex-col gap-4 mb-4">
						<div>
							<h1 className="text-white mb-2 flex items-center gap-3 text-2xl">
								National Coverage Map
								<span
									className="px-3 py-1 bg-[#A7FF5E]/10 border border-[#A7FF5E]/30 rounded-full text-[#A7FF5E] text-xs"
									style={{ width: "3.5rem" }}
								>
									Live
								</span>
							</h1>
							<p className="text-[#8ba894] text-sm">
								Real-time property lien coverage across the
								United States
							</p>
						</div>

						{/* Data Freshness */}
						{mapStats && (
							<div className="flex items-center gap-3 bg-[#1f3126]/50 backdrop-blur-sm px-4 py-2 rounded-xl border border-[#A7FF5E]/20 shadow-lg w-fit">
								<div className="w-2 h-2 bg-[#A7FF5E] rounded-full animate-pulse"></div>
								<div>
									<div className="text-[#A7FF5E] text-xs uppercase tracking-wide">
										Data Freshness
									</div>
									<div className="text-white text-sm">
										{formatDate(mapStats.lastUpdated)}
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Quick Stats Grid - Horizontal Scroll on Mobile */}
					{mapStats && (
						<div className="overflow-x-auto -mx-4 px-4 pb-2">
							<div className="flex gap-3 min-w-max">
								<div className="group relative bg-gradient-to-br from-[#1f3126] to-[#16251c] p-4 rounded-xl border border-[#2a3f32] shadow-lg overflow-hidden min-w-[200px]">
									<div className="absolute top-0 right-0 w-20 h-20 bg-[#A7FF5E]/5 rounded-full blur-2xl"></div>
									<div className="relative">
										<div className="text-[#8ba894] text-xs uppercase tracking-wide mb-2">
											Total Properties
										</div>
										<div className="text-[#A7FF5E] text-2xl mb-1">
											{formatNumber(
												mapStats.totalProperties
											)}
										</div>
										<div className="text-[#8ba894] text-xs">
											Based on last 30 days of auction
											data
										</div>
									</div>
								</div>

								<div className="group relative bg-gradient-to-br from-[#1f3126] to-[#16251c] p-4 rounded-xl border border-[#2a3f32] shadow-lg overflow-hidden min-w-[200px]">
									<div className="absolute top-0 right-0 w-20 h-20 bg-[#A7FF5E]/5 rounded-full blur-2xl"></div>
									<div className="relative">
										<div className="text-[#8ba894] text-xs uppercase tracking-wide mb-2">
											Counties Covered
										</div>
										<div className="text-[#A7FF5E] text-2xl mb-1">
											{formatNumber(
												mapStats.totalCounties
											)}
										</div>
										<div className="text-[#8ba894] text-xs">
											Across {mapStats.totalStates} states
										</div>
									</div>
								</div>

								<div className="group relative bg-gradient-to-br from-[#1f3126] to-[#16251c] p-4 rounded-xl border border-[#2a3f32] shadow-lg overflow-hidden min-w-[200px]">
									<div className="absolute top-0 right-0 w-20 h-20 bg-[#A7FF5E]/5 rounded-full blur-2xl"></div>
									<div className="relative">
										<div className="text-[#8ba894] text-xs uppercase tracking-wide mb-2">
											National Coverage
										</div>
										<div className="text-[#A7FF5E] text-2xl mb-1">
											{mapStats.coverage}%
										</div>
										<div className="w-full bg-[#2a3f32] rounded-full h-1.5 mt-2">
											<div
												className="bg-gradient-to-r from-[#A7FF5E] to-[#cbf7a6] h-1.5 rounded-full"
												style={{
													width: `${mapStats.coverage}%`,
												}}
											></div>
										</div>
									</div>
								</div>

								<div className="group relative bg-gradient-to-br from-[#A7FF5E]/10 via-[#1f3126] to-[#16251c] p-4 rounded-xl border border-[#A7FF5E]/30 shadow-lg overflow-hidden min-w-[200px]">
									<div className="absolute top-0 right-0 w-20 h-20 bg-[#A7FF5E]/10 rounded-full blur-2xl"></div>
									<div className="relative">
										<div className="text-[#A7FF5E] text-xs uppercase tracking-wide mb-2">
											Avg. Properties/County
										</div>
										<div className="text-white text-2xl mb-1">
											{formatNumber(
												Math.round(
													mapStats.totalProperties /
														mapStats.totalCounties
												)
											)}
										</div>
										<div className="text-[#cbf7a6] text-xs">
											High-density markets
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Main Content - Map */}
			<div className="">
				<div
					className="relative"
					style={{ height: "calc(100vh - 280px)" }}
				>
					{/* Loading Overlay */}
					{isLoading && (
						<div className="absolute inset-0 flex items-center justify-center bg-[#1f3126] rounded-2xl border border-[#2a3f32] z-10 shadow-2xl">
							<div className="text-center">
								<div className="w-12 h-12 border-4 border-[#2a3f32] border-t-[#A7FF5E] rounded-full animate-spin mx-auto mb-4"></div>
								<div className="text-[#A7FF5E] text-sm">
									Loading Coverage Data...
								</div>
								<div className="text-[#8ba894] text-xs mt-2">
									Fetching latest property information
								</div>
							</div>
						</div>
					)}

					{/* Map Container */}
					<div className="relative h-full rounded-xl overflow-hidden shadow-2xl border border-[#2a3f32]">
						<div
							ref={mapContainerRef}
							className="w-full h-full bg-[#1f3126]"
						></div>

						{/* Map Legend */}
						<div className="absolute bottom-4 left-4 bg-[#16251c]/95 backdrop-blur-md p-3 rounded-lg border border-[#2a3f32] shadow-xl max-w-[260px]">
							<div className="text-xs mb-2 text-white">
								Property Density
							</div>
							<div className="space-y-2">
								<div className="flex items-center gap-2">
									<div className="w-6 h-6 bg-[#A7FF5E] rounded-md shadow-lg shadow-[#A7FF5E]/30"></div>
									<div>
										<div className="text-white text-xs">
											High Density
										</div>
										<div className="text-[10px] text-[#8ba894]">
											{">"}500 properties
										</div>
									</div>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-6 h-6 bg-[#cbf7a6]/60 rounded-md shadow-lg"></div>
									<div>
										<div className="text-white text-xs">
											Medium
										</div>
										<div className="text-[10px] text-[#8ba894]">
											100-500 properties
										</div>
									</div>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-6 h-6 bg-[#8ba894]/40 rounded-md"></div>
									<div>
										<div className="text-white text-xs">
											Low
										</div>
										<div className="text-[10px] text-[#8ba894]">
											1-100 properties
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Zoom Info */}
						<div className="absolute top-4 left-4 bg-[#16251c]/95 backdrop-blur-md px-3 py-2 rounded-lg border border-[#A7FF5E]/30 shadow-lg">
							<div className="flex items-center gap-2 text-[#A7FF5E] text-xs">
								<Info className="w-3 h-3" />
								<span>Tap markers</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Region Detail Modal - Full screen on mobile */}
			{selectedRegion && (
				<div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-0">
					<div className="bg-[#16251c] border-0 rounded-none w-full h-full overflow-y-auto">
						{/* Modal Header */}
						<div className="sticky top-0 bg-[#16251c] border-b border-[#2a3f32] p-4 flex items-start justify-between z-10">
							<div>
								<h2 className="text-white mb-1 text-xl">
									{selectedRegion.county},{" "}
									{selectedRegion.state}
								</h2>
								<p className="text-[#8ba894] text-sm">
									Property Lien Coverage Details
								</p>
							</div>
							<button
								onClick={() => setSelectedRegion(null)}
								className="text-[#8ba894] hover:text-white transition-colors p-2 -m-2 touch-manipulation"
							>
								<X className="w-6 h-6" />
							</button>
						</div>

						{/* Modal Content */}
						<div className="p-4">
							{/* Key Metrics */}
							<div className="grid grid-cols-1 gap-3 mb-4">
								<div className="bg-[#1f3126] p-4 rounded-lg border border-[#2a3f32]">
									<div className="text-[#8ba894] text-sm mb-1">
										Total Properties
									</div>
									<div className="text-[#A7FF5E] text-2xl mb-1">
										{formatNumber(
											selectedRegion.totalLiens
										)}
									</div>
									<div className="text-xs text-[#8ba894]">
										In this county
									</div>
								</div>
								<div className="bg-[#1f3126] p-4 rounded-lg border border-[#2a3f32]">
									<div className="text-[#8ba894] text-sm mb-1">
										With Parcel Data
									</div>
									<div className="text-[#A7FF5E] text-2xl mb-1">
										{formatNumber(
											selectedRegion.propertiesWithParcelData
										)}
									</div>
									<div className="text-xs text-[#8ba894]">
										{Math.round(
											(selectedRegion.propertiesWithParcelData /
												selectedRegion.totalLiens) *
												100
										)}
										% coverage
									</div>
								</div>
								<div className="bg-[#1f3126] p-4 rounded-lg border border-[#2a3f32]">
									<div className="text-[#8ba894] text-sm mb-1">
										Last Updated
									</div>
									<div className="text-[#A7FF5E] text-xl mb-1">
										{formatDate(
											selectedRegion.lastUpdated
										).replace("Updated ", "")}
									</div>
									<div className="text-xs text-[#8ba894]">
										Records verified
									</div>
								</div>
							</div>

							{/* Access Notice */}
							<div className="bg-gradient-to-r from-[#A7FF5E]/10 to-transparent p-4 rounded-lg border border-[#A7FF5E]/30 mb-4">
								<div className="flex items-start gap-3">
									<Info className="w-5 h-5 text-[#A7FF5E] shrink-0 mt-0.5" />
									<div>
										<div className="text-white text-sm mb-1">
											Premium Data Available
										</div>
										<p className="text-[#8ba894] text-sm">
											Access complete property details
											including owner information, lien
											amounts, auction dates, and market
											analysis. Subscribe to unlock all{" "}
											{formatNumber(
												selectedRegion.totalLiens
											)}{" "}
											properties in this region.
										</p>
									</div>
								</div>
							</div>

							{/* CTA Buttons */}
							<div className="flex flex-col gap-3">
								<button className="flex-1 bg-[#A7FF5E] text-[#16251c] px-6 py-3 rounded-lg active:bg-[#cbf7a6] transition-colors shadow-lg touch-manipulation text-sm">
									Access Properties in This Region
								</button>
								<button className="px-6 py-3 border border-[#2a3f32] rounded-lg text-white active:bg-[#1f3126] transition-colors touch-manipulation text-sm">
									Get More Info
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
